import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const VERSION_KEY = 'app_version';

async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Silently reloads the page when a new app version is detected.
 * Checks on every route navigation (location change).
 *
 * On first load it stores the current build version.
 * On subsequent navigations it compares and hard-reloads if the version changed.
 */
export function useAutoVersionCheck() {
  const location = useLocation();
  const checkedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const serverVersion = await fetchServerVersion();
      if (cancelled || !serverVersion) return;

      const storedVersion = sessionStorage.getItem(VERSION_KEY);

      if (!storedVersion) {
        // First visit — just store the version
        sessionStorage.setItem(VERSION_KEY, serverVersion);
        checkedRef.current = true;
        return;
      }

      if (storedVersion !== serverVersion) {
        // New version deployed — clear caches and hard-reload
        sessionStorage.setItem(VERSION_KEY, serverVersion);

        // Clear browser caches if available
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((key) => caches.delete(key)));
        }

        // Hard reload to fetch fresh assets
        window.location.reload();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
}
